import flattenDeep from 'lodash/flattenDeep';
import { useMemo } from 'react';
import useSelections from '@/components/widget/misc/useSelections';
import useSubscribeWidgetResults from '@/components/widget/visualizations/hooks/useSubscribeWidgetResults';
import { encodeSelections } from '@/utils/widget/parseSelections';
import { getSelectionGroupItemByIndex } from '@/components/Editor/Form/utils';
import { DEFAULT_COLORS, colorPallets, selectionConfig } from '@/components/widget/visualizations/GaugeChart';
import { useGaugeChartStyles } from '@/components/widget/visualizations/GaugeChart/stylesTheme';
import Ranges from '@/components/Editor/Form/GaugeChartFields/Ranges';
import Animation from '@/components/Editor/Form/fields/Animation';
import ChartLabels from '@/components/Editor/Form/fields/ChartLabels';
import ColorPalletMenu from '@/components/Editor/Form/fields/ColorPalletMenu';
import { TemplateRevealPill } from '@/components/Editor/Form/fields/Template';
import ChartType from './ChartType';
import { jsx as _jsx } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";

const WidgetEditFormGaugeChartFields = _ref => {
  var _config$globalSeriesO;

  let {
    widgetId,
    sectionId,
    reportingWidgetId,
    config,
    pillVariant,
    onChange,
    onEditSelections
  } = _ref;
  const nameSpace = `gauge-chart-${widgetId}-${sectionId}`;
  const templateFieldId = `${nameSpace}-template`;
  const {
    themeCtx: {
      colorPallet: defaultColorPallet = DEFAULT_COLORS
    }
  } = useGaugeChartStyles(config);
  const {
    colorPallet = defaultColorPallet,
    selections = '',
    labelsSelections = ''
  } = config; // Get current selection results

  const results = useSubscribeWidgetResults(reportingWidgetId); // Apply selections to results

  const {
    selectionResults
  } = useSelections({
    results,
    selections: selections || '',
    selectionsLabeled: false,
    selectionConfig
  });
  const exampleValue = useMemo(() => {
    const exampleSelection = getSelectionGroupItemByIndex(flattenDeep(selectionResults), 0);
    return typeof exampleSelection.value !== 'undefined' ? exampleSelection.value : 10;
  }, [selectionResults]);
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [/*#__PURE__*/_jsx(ColorPalletMenu, {
      label: "Colors",
      colors: colorPallet,
      colorPallets: colorPallets,
      palletColorOverrides: config.palletColorOverrides,
      openButtonVariant: pillVariant,
      onOverridePalletColors: (name, colors) => onChange('palletColorOverrides', {
        [name]: colors
      }),
      onChange: colorPallet => onChange('', {
        colorPallet
      })
    }), onEditSelections ? /*#__PURE__*/_jsx(ChartLabels, {
      nameSpace: nameSpace,
      selections: labelsSelections ? encodeSelections(labelsSelections || []) : undefined,
      defaultSelections: config.defaultLabelsSelections,
      openButtonVariant: pillVariant,
      onChange: onChange,
      onEditSelections: () => onEditSelections('labels')
    }) : /*#__PURE__*/_jsx(_Fragment, {}), /*#__PURE__*/_jsx(TemplateRevealPill, {
      id: templateFieldId,
      label: "Formatting",
      caption: "Default formatting. Can be overwritten by other formatting settings.",
      exampleValue: exampleValue,
      template: (_config$globalSeriesO = config.globalSeriesOptions) === null || _config$globalSeriesO === void 0 ? void 0 : _config$globalSeriesO.template,
      openButtonVariant: pillVariant,
      onChange: value => onChange('globalSeriesOptions', {
        template: value
      })
    }), /*#__PURE__*/_jsx(ChartType, {
      nameSpace: nameSpace,
      config: config,
      openButtonVariant: pillVariant,
      onChange: onChange
    }), /*#__PURE__*/_jsx(Ranges, {
      nameSpace: nameSpace,
      ranges: config.ranges,
      colorPallet: colorPallet,
      openButtonVariant: pillVariant,
      onChange: ranges => onChange('', {
        ranges
      })
    }), /*#__PURE__*/_jsx(Animation, {
      nameSpace: nameSpace,
      animation: config.animation,
      defaults: {
        duration: 3000,
        delay: 100
      },
      hasEasing: false,
      openButtonVariant: pillVariant,
      onChange: update => onChange('animation', update)
    })]
  });
};

export default WidgetEditFormGaugeChartFields;